import React, { useMemo } from 'react';
import {
  FieldContext,
  FilterType,
  IFieldFieldsFragment,
  IFilterFieldsFragment,
  IPredicateFieldsFragment,
} from '../../../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { setBreakdownGroupFilter } from '../../../../../../redux/slices/oldBuilder';
import {
  activeSchemaIdSelector,
  defaultLogOpsSelector,
} from '../../../../../../redux/selectors/builder';
import { FilterFieldsView } from '../../../../../FilterFields';
import { defaultEventInput } from '../../../../../../utils/builder';
import { assertCondition } from '../../../../../../utils/types';
import FilterWidget from '../../../../../FilterWidget';
import { IPredicateContext } from '../../../../../FilterWidget/types';
import Popover from '../../../../../Popover';
import {
  useHighlightingContext,
  withHighlightingContext,
} from '../../../../../HighlightingContext';
import { useActionsVisibilityContext } from '../../../../../ActionsVisibilityContext';
import usePopoverController from '../../../../../Popover/usePopoverController';

interface IProps {
  field: IFieldFieldsFragment;
  predicate: IPredicateFieldsFragment;
  groupIndex: number;
}

const predicateContext: IPredicateContext = {
  fieldContext: FieldContext.Breakdown,
  events: [defaultEventInput],
  func: null,
};

const BreakdownGroupFilterView = ({ field, predicate, groupIndex }: IProps) => {
  const { closePopover, isPopoverOpen, openPopover, popoverAnchor } =
    usePopoverController<HTMLDivElement>();

  const dispatch = useAppDispatch();

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const defaultLogOps = useAppSelector(defaultLogOpsSelector);

  const { deactivateHighlighting } = useHighlightingContext();
  const { hideActions } = useActionsVisibilityContext();

  const filter: IFilterFieldsFragment = useMemo(
    () => ({
      __typename: 'BasicFilter',
      conditions: [
        {
          __typename: 'Condition',
          logicOps: [],
          predicates: [predicate],
        },
      ],
      logicOps: [],
      type: FilterType.Basic,
    }),
    [predicate],
  );

  const handleEditFilterClick = () => {
    openPopover();

    deactivateHighlighting();
    hideActions();
  };

  const handleRemove = () => {
    dispatch(
      setBreakdownGroupFilter({
        groupIndex,
        filter: null,
      }),
    );
  };

  const handleSubmitNewFilters = (newFilter: IFilterFieldsFragment) => {
    assertCondition(newFilter.__typename === 'BasicFilter');

    const predicate = newFilter.conditions[0].predicates[0];

    dispatch(
      setBreakdownGroupFilter({
        groupIndex,
        filter: predicate,
      }),
    );

    closePopover();
  };

  return (
    <>
      <FilterFieldsView
        ref={popoverAnchor}
        filter={filter}
        onEdit={handleEditFilterClick}
        onRemove={handleRemove}
        schemaId={activeSchemaId}
      />
      {isPopoverOpen && (
        <Popover anchor={popoverAnchor.current} isOpen close={closePopover}>
          <FilterWidget
            onSubmit={handleSubmitNewFilters}
            schemaId={activeSchemaId}
            context={predicateContext}
            lockedField={field}
            maxConditions={1}
            maxPredicates={1}
            defaultFilter={filter}
            disableNamedFilters={true}
            defaultLogOps={defaultLogOps}
          />
        </Popover>
      )}
    </>
  );
};

export default withHighlightingContext(BreakdownGroupFilterView);
